import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/Combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface ConditionRowProps {
  condition: FieldCondition;
  onChange: (c: FieldCondition) => void;
  onDelete?: () => void;
  questions: Record<string, PolicyFormQuestion>;
}

const DisplaySpan = ({ questionRef, title }: { questionRef: string; title: string }) => {
  return (
    <div className="flex w-64 flex-col items-start whitespace-normal break-words">
      <span className="text-sm font-medium leading-tight">{questionRef}</span>
      <span className="text-muted-foreground text-xs leading-snug text-gray-700">{title}</span>
    </div>
  );
};

export const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  onChange,
  onDelete,
  questions,
}) => {
  const [field, setField] = useState(condition.field);
  const [operator, setOperator] = useState(condition.operator);
  const [value, setValue] = useState((condition.value as string) || '');
  const [selectedQuestion, setSelectedQuestion] = useState<PolicyFormQuestion | null>(
    questions[condition.field],
  );

  const handleSelectQuestion = (questionRef: string) => {
    setSelectedQuestion(questions[questionRef]);
    setField(questionRef);
    setValue('');
  };

  useEffect(() => {
    onChange({ field, operator, value });
  }, [field, operator, value]);

  useEffect(() => {
    if (operator === 'empty' || operator === 'notEmpty') {
      setValue('');
    }
  }, [operator]);

  const comboBoxOption = Object.values(questions).map(question => ({
    value: question.questionRef,
    label: question.questionRef,
    selectedDisplay: question.questionRef,
    optionDisplay: (
      <DisplaySpan
        questionRef={question.questionRef}
        title={question.title}
      />
    ),
  }));

  return (
    <div className="mt-1 flex items-center gap-2">
      <Combobox
        value={field}
        className="w-full"
        options={comboBoxOption}
        onChange={f => handleSelectQuestion(f as string)}
      />

      <Select
        value={operator}
        onValueChange={op => setOperator(op as ComparisonOperator)}
      >
        <SelectTrigger className="w-36">
          <SelectValue placeholder="Select operator" />
        </SelectTrigger>
        <SelectContent>
          {[
            'equals',
            'notEquals',
            'contains',
            'notContains',
            'in',
            'notIn',
            'empty',
            'notEmpty',
          ].map(op => (
            <SelectItem
              key={op}
              value={op}
            >
              {op}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input */}

      {!['empty', 'notEmpty'].includes(operator) &&
        (selectedQuestion?.type === 'select' || selectedQuestion?.type === 'radio' ? (
          <Select
            value={value}
            onValueChange={v => setValue(v)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent>
              {selectedQuestion?.options?.map(op => (
                <SelectItem
                  key={op.id}
                  value={op.id}
                >
                  {op.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <input
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            className="w-32 rounded border px-2 py-1"
            placeholder="Value"
          />
        ))}

      {onDelete && (
        <Button
          size="sm"
          variant="destructive"
          onClick={onDelete}
        >
          <X />
        </Button>
      )}
    </div>
  );
};
